import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, RefreshCw } from 'lucide-react';
import axios from 'axios';
import './ChatWidget.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Generate or retrieve session ID
    let sid = localStorage.getItem('frost_chat_session');
    if (!sid) {
      sid = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('frost_chat_session', sid);
    }
    setSessionId(sid);

    // Add welcome message
    setMessages([{
      role: 'assistant',
      content: 'Hallo! Ich bin dein Ansprechpartner für alle Fragen rund um Frost Studio. Wie kann ich dir helfen?',
      timestamp: new Date()
    }]);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleRefresh = () => {
    // Generate new session ID
    const newSid = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('frost_chat_session', newSid);
    setSessionId(newSid);
    
    // Reset messages
    setMessages([{
      role: 'assistant',
      content: 'Hallo! Ich bin dein Ansprechpartner für alle Fragen rund um Frost Studio. Wie kann ich dir helfen?',
      timestamp: new Date()
    }]);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: inputMessage
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Entschuldigung, es gab einen Fehler. Bitte versuche es erneut.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        className="chat-toggle-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle chat"
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="chat-widget-modern">
          <div className="chat-header-modern">
            <div className="chat-header-left">
              <img src="/frost-logo.png" alt="Frost Studio" className="chat-logo-modern" />
              <div className="chat-header-text">
                <span className="chat-title-modern">Frost Studio AI-Support</span>
              </div>
            </div>
            <div className="chat-header-actions">
              <button onClick={handleRefresh} className="chat-action-btn" title="Chat zurücksetzen">
                <RefreshCw size={20} />
              </button>
            </div>
          </div>

          <div className="chat-messages-modern">
            {messages.map((msg, index) => (
              <div key={index} className={`chat-message-modern ${msg.role}`}>
                <div className="message-bubble">
                  {msg.content}
                  <div className="message-time">
                    {new Date(msg.timestamp).toLocaleTimeString('de-DE', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="chat-message-modern assistant">
                <div className="message-bubble typing">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-container-modern">
            <input
              type="text"
              className="chat-input-modern"
              placeholder="Stelle eine Frage..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
            />
            <button
              className="chat-send-btn-modern"
              onClick={handleSendMessage}
              disabled={isLoading || !inputMessage.trim()}
              aria-label="Send message"
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;